import { useEffect, useRef, useState } from "react";
export type IVROption = {
  key: string;
  digit: string;
  label: string;
  lineId: string;
  action: "ring" | "voicemail" | "announcement";
  announcement?: string;
};
export type IVRConfig = {
  name: string;
  greeting: string;
  ringSeconds: number;
  fallbackKey: string;
  options: IVROption[];
};
type Profile = {
  lineId: string;
  revision: number;
  config: IVRConfig;
  updatedAt: string;
  deleted: boolean;
};
type Line = {
  lineId: string;
  endpoint: string;
  number: string;
  label: string;
  canAttach: boolean;
};
type Message = {
  id: string;
  folder: string;
  receivedAt: string | null;
  durationSeconds: number | null;
};
type Snapshot = {
  items: Profile[];
  lines: Line[];
  mailboxes?: {
    lineId: string;
    optionKey: string;
    label: string;
    messages: Message[];
  }[];
};
type Api = <T>(
  path: string,
  options?: {
    method?: string;
    body?: unknown;
    csrf?: string | undefined;
    signal?: AbortSignal;
  },
) => Promise<T>;
export function IVROptionFields({
  option,
  lines,
  disabled,
  onChange,
  onDelete,
}: {
  option: IVROption;
  lines: Line[];
  disabled: boolean;
  onChange: (o: IVROption) => void;
  onDelete: () => void;
}) {
  return (
    <fieldset className="cr-ivr-option" disabled={disabled}>
      <legend>{option.label || "New menu option"}</legend>
      <label>
        Key
        <select
          aria-label={`Key for ${option.label || "new option"}`}
          value={option.digit}
          onChange={(e) => onChange({ ...option, digit: e.target.value })}
        >
          {Array.from({ length: 9 }, (_, i) => (
            <option key={i} value={String(i)}>
              {i}
            </option>
          ))}
        </select>
      </label>
      <label>
        Department or option name
        <input
          maxLength={80}
          required
          value={option.label}
          onChange={(e) => onChange({ ...option, label: e.target.value })}
        />
      </label>
      <label>
        Action
        <select
          value={option.action}
          onChange={(e) =>
            onChange({
              ...(Object.fromEntries(
                Object.entries(option).filter(
                  ([key]) => key !== "announcement",
                ),
              ) as IVROption),
              action: e.target.value as IVROption["action"],
              ...(e.target.value === "announcement"
                ? { announcement: option.announcement ?? "" }
                : {}),
            })
          }
        >
          <option value="ring">Ring phone, then voicemail</option>
          <option value="voicemail">Go directly to voicemail</option>
          <option value="announcement">
            Play announcement, then return to menu
          </option>
        </select>
      </label>
      {option.action === "announcement" ? (
        <label>
          Announcement text
          <textarea
            required
            maxLength={2000}
            value={option.announcement ?? ""}
            onChange={(e) =>
              onChange({ ...option, announcement: e.target.value })
            }
          />
        </label>
      ) : (
        <label>
          Phone destination
          <select
            required
            value={option.lineId}
            onChange={(e) => onChange({ ...option, lineId: e.target.value })}
          >
            {!lines.some((l) => l.lineId === option.lineId) && (
              <option value="">Choose a connected phone</option>
            )}
            {lines.map((l) => (
              <option key={l.lineId} value={l.lineId}>
                {l.label} · {l.endpoint}
              </option>
            ))}
          </select>
        </label>
      )}
      <button type="button" className="cr-button secondary" onClick={onDelete}>
        Delete option
      </button>
    </fieldset>
  );
}
export function IVRPanel({
  api,
  accountId,
  accountName,
  customer,
  csrf,
  readOnly,
}: {
  api: Api;
  accountId: string;
  accountName: string;
  customer: boolean;
  csrf: string | undefined;
  readOnly: boolean;
}) {
  const base = `/${customer ? "customer" : "admin"}/accounts/${accountId}/ivrs`;
  const [audio, setAudio] = useState<{ id: string; base64: string } | null>(
    null,
  );
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null),
    [error, setError] = useState(""),
    [notice, setNotice] = useState("");
  const [lineId, setLineId] = useState(""),
    [draft, setDraft] = useState<IVRConfig | null>(null),
    [revision, setRevision] = useState(0);
  const [busy, setBusy] = useState(false),
    [refresh, setRefresh] = useState(0),
    [confirmDelete, setConfirmDelete] = useState(false);
  const command = useRef({ fingerprint: "", key: "" }),
    working = useRef(false);
  useEffect(() => {
    const abort = new AbortController();
    setAudio(null);
    setSnapshot(null);
    setDraft(null);
    setLineId("");
    setError("");
    void api<Snapshot>(base, { signal: abort.signal })
      .then((v) => {
        if (!abort.signal.aborted) setSnapshot(v);
      })
      .catch((e: Error) => {
        if (!abort.signal.aborted) setError(e.message);
      });
    return () => abort.abort();
  }, [api, base, refresh]);
  function edit(id: string) {
    if (!snapshot) return;
    const p = snapshot.items.find((p) => p.lineId === id);
    setLineId(id);
    setRevision(p?.revision ?? 0);
    setConfirmDelete(false);
    setError("");
    setNotice("");
    if (p && !p.deleted) setDraft(structuredClone(p.config));
    else {
      const key = crypto.randomUUID();
      setDraft({
        name: "Main menu",
        greeting: `Thank you for calling ${accountName}.`,
        ringSeconds: 25,
        fallbackKey: key,
        options: [
          { key, digit: "0", label: "our office", lineId: id, action: "ring" },
        ],
      });
    }
  }
  function submit(operation: "save" | "delete") {
    if (!draft || working.current || readOnly) return;
    if (operation === "save") {
      if (
        !draft.options.length ||
        new Set(draft.options.map((o) => o.digit)).size !==
          draft.options.length ||
        !draft.options.some((o) => o.key === draft.fallbackKey)
      ) {
        setError("Use unique keys and choose an existing fallback option.");
        return;
      }
      if (
        !draft.options.every(
          (o) =>
            o.label.trim() &&
            snapshot?.lines.some((l) => l.lineId === o.lineId),
        )
      ) {
        setError("Give each option a name and a connected phone destination.");
        return;
      }
    }
    const body = {
      lineId,
      expectedRevision: revision,
      ...(operation === "save" ? { config: draft } : {}),
    };
    const fingerprint = JSON.stringify({ operation, body });
    if (command.current.fingerprint !== fingerprint)
      command.current = { fingerprint, key: crypto.randomUUID() };
    working.current = true;
    setBusy(true);
    setError("");
    setNotice("");
    void api<Profile>(`${base}/${operation}`, {
      method: "POST",
      csrf,
      body: { ...body, requestKey: command.current.key },
    })
      .then((p) => {
        setSnapshot((s) =>
          s
            ? {
                ...s,
                items: [...s.items.filter((x) => x.lineId !== p.lineId), p],
              }
            : s,
        );
        setRevision(p.revision);
        setConfirmDelete(false);
        command.current = { fingerprint: "", key: "" };
        if (operation === "delete") {
          setDraft(null);
          setLineId("");
          setNotice(
            "IVR deleted. Calls now use the line's regular routing. Existing voicemail messages were retained.",
          );
        } else {
          setDraft(structuredClone(p.config));
          setNotice("IVR saved and applied. New callers will hear this menu.");
        }
      })
      .catch((e: Error) =>
        setError(`${e.message} Refresh the IVR status before retrying.`),
      )
      .finally(() => {
        working.current = false;
        setBusy(false);
      });
  }
  const active = snapshot?.items.find((p) => p.lineId === lineId && !p.deleted);
  return (
    <section className="cr-panel" aria-label="Phone menus">
      <h3>Phone menus (IVR)</h3>
      <p>
        Create a greeting and keypad menu for this customer's incoming number.
        Key 9 repeats the menu. Two missed or invalid selections go to the
        fallback option.
      </p>
      {error && (
        <p className="cr-notice error" role="alert">
          {error}
        </p>
      )}
      {notice && (
        <p className="cr-notice" role="status">
          {notice}
        </p>
      )}
      {!snapshot && !error && <p role="status">Loading phone menus…</p>}
      {snapshot && (
        <>
          {snapshot.items
            .filter((p) => !p.deleted)
            .map((p) => (
              <div key={p.lineId} className="cr-ivr-summary">
                <div>
                  <strong>{p.config.name}</strong>
                  <p>
                    {snapshot.lines.find((l) => l.lineId === p.lineId)
                      ?.number || "Incoming line"}{" "}
                    · {p.config.options.length} options · Updated{" "}
                    {new Date(p.updatedAt).toLocaleString()}
                  </p>
                </div>
                <button
                  type="button"
                  className="cr-button secondary"
                  disabled={busy}
                  onClick={() => edit(p.lineId)}
                >
                  {readOnly ? "View menu" : "Edit menu"}
                </button>
              </div>
            ))}
          {!snapshot.items.some((p) => !p.deleted) && (
            <p>No phone menus have been created for this customer.</p>
          )}
          {!readOnly && (
            <label>
              Add an IVR to an incoming line
              <select
                value=""
                disabled={busy}
                onChange={(e) => {
                  if (e.target.value) edit(e.target.value);
                }}
              >
                <option value="">Choose a number…</option>
                {snapshot.lines
                  .filter(
                    (l) =>
                      l.canAttach &&
                      !snapshot.items.some(
                        (p) => p.lineId === l.lineId && !p.deleted,
                      ),
                  )
                  .map((l) => (
                    <option value={l.lineId} key={l.lineId}>
                      {l.number} · {l.label}
                    </option>
                  ))}
              </select>
            </label>
          )}
          {!snapshot.lines.some((l) => l.canAttach) && (
            <p>
              An incoming number and its phone destination must be installed
              before an IVR can be added.
            </p>
          )}
          {(snapshot.mailboxes ?? []).length > 0 && (
            <section aria-label="Department voicemail">
              <h4>Department voicemail</h4>
              {snapshot.mailboxes?.map((box) => (
                <div key={box.lineId + box.optionKey}>
                  <strong>{box.label}</strong>
                  {box.messages.length === 0 ? (
                    <p>No messages.</p>
                  ) : (
                    box.messages.map((message) => (
                      <div key={message.id}>
                        <p>
                          {message.receivedAt
                            ? new Date(message.receivedAt).toLocaleString()
                            : "Received message"}{" "}
                          · {message.durationSeconds ?? 0} seconds
                        </p>
                        {!readOnly && (
                          <button
                            type="button"
                            className="cr-button secondary"
                            disabled={busy}
                            onClick={() => {
                              setBusy(true);
                              setAudio(null);
                              void api<{ messageId: string; base64: string }>(
                                `${base}/audio`,
                                {
                                  method: "POST",
                                  csrf,
                                  body: {
                                    lineId: box.lineId,
                                    optionKey: box.optionKey,
                                    messageId: message.id,
                                  },
                                },
                              )
                                .then((v) =>
                                  setAudio({
                                    id: v.messageId,
                                    base64: v.base64,
                                  }),
                                )
                                .catch((e: Error) => setError(e.message))
                                .finally(() => setBusy(false));
                            }}
                          >
                            Listen
                          </button>
                        )}
                        {audio?.id === message.id && (
                          <audio
                            controls
                            aria-label={`Voicemail for ${box.label}`}
                            src={`data:audio/wav;base64,${audio.base64}`}
                          />
                        )}
                      </div>
                    ))
                  )}
                </div>
              ))}
            </section>
          )}
          {draft && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                submit("save");
              }}
            >
              <fieldset disabled={busy || readOnly} className="cr-ivr-form">
                <legend>{active ? "Edit IVR" : "New IVR"}</legend>
                <label>
                  Menu name
                  <input
                    value={draft.name}
                    maxLength={100}
                    required
                    onChange={(e) =>
                      setDraft({ ...draft, name: e.target.value })
                    }
                  />
                </label>
                <label>
                  Opening greeting
                  <textarea
                    rows={3}
                    maxLength={1000}
                    required
                    value={draft.greeting}
                    onChange={(e) =>
                      setDraft({ ...draft, greeting: e.target.value })
                    }
                  />
                </label>
                <p>
                  OpenAI Marin reads the option names and keys below after your
                  greeting automatically.
                </p>
                <label>
                  Ring time before voicemail (seconds)
                  <input
                    type="number"
                    min={5}
                    max={60}
                    required
                    value={draft.ringSeconds}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        ringSeconds: Number(e.target.value),
                      })
                    }
                  />
                </label>
                {draft.options.map((o) => (
                  <IVROptionFields
                    key={o.key}
                    option={o}
                    lines={snapshot.lines}
                    disabled={busy || readOnly}
                    onChange={(updated) =>
                      setDraft({
                        ...draft,
                        options: draft.options.map((x) =>
                          x.key === o.key ? updated : x,
                        ),
                      })
                    }
                    onDelete={() => {
                      const options = draft.options.filter(
                        (x) => x.key !== o.key,
                      );
                      setDraft({
                        ...draft,
                        options,
                        fallbackKey:
                          draft.fallbackKey === o.key
                            ? (options[0]?.key ?? "")
                            : draft.fallbackKey,
                      });
                    }}
                  />
                ))}
                <button
                  type="button"
                  className="cr-button secondary"
                  disabled={draft.options.length >= 9}
                  onClick={() => {
                    const key = crypto.randomUUID();
                    const digit =
                      Array.from({ length: 9 }, (_, i) => String(i)).find(
                        (d) => !draft.options.some((o) => o.digit === d),
                      ) ?? "0";
                    setDraft({
                      ...draft,
                      options: [
                        ...draft.options,
                        { key, digit, label: "", lineId, action: "ring" },
                      ],
                      fallbackKey: draft.fallbackKey || key,
                    });
                  }}
                >
                  Add menu option
                </button>
                <label>
                  No response or invalid selection
                  <select
                    value={draft.fallbackKey}
                    required
                    onChange={(e) =>
                      setDraft({ ...draft, fallbackKey: e.target.value })
                    }
                  >
                    {!draft.options.length && (
                      <option value="">Add an option first</option>
                    )}
                    {draft.options.map((o) => (
                      <option key={o.key} value={o.key}>
                        {o.digit} — {o.label || "Unnamed option"}
                      </option>
                    ))}
                  </select>
                </label>
                <p>
                  This menu is available 24 hours a day. Unanswered or offline
                  phones go to that option's voicemail.
                </p>
                {!readOnly && (
                  <button
                    className="cr-button"
                    type="submit"
                    disabled={!draft.options.length}
                  >
                    {busy ? "Applying IVR…" : "Save and apply IVR"}
                  </button>
                )}
              </fieldset>
              {active && !readOnly && !confirmDelete && (
                <button
                  type="button"
                  className="cr-button secondary"
                  disabled={busy}
                  onClick={() => setConfirmDelete(true)}
                >
                  Delete IVR
                </button>
              )}
              {confirmDelete && (
                <div className="cr-notice">
                  <p>
                    Delete this IVR and restore regular routing to the assigned
                    phone? Existing messages will be retained.
                  </p>
                  <button
                    type="button"
                    className="cr-button"
                    disabled={busy}
                    onClick={() => submit("delete")}
                  >
                    Delete and restore regular routing
                  </button>{" "}
                  <button
                    type="button"
                    className="cr-button secondary"
                    disabled={busy}
                    onClick={() => setConfirmDelete(false)}
                  >
                    Keep IVR
                  </button>
                </div>
              )}
            </form>
          )}
        </>
      )}
      <button
        type="button"
        className="cr-button secondary"
        disabled={busy}
        onClick={() => setRefresh((v) => v + 1)}
      >
        Refresh IVR status
      </button>
    </section>
  );
}
